import { Google2DImageryProvider, ImageryLayer, MapboxStyleImageryProvider } from 'cesium'
import type { Viewer } from 'cesium'
import log from '@/utils/useLogger'

// Cache for imagery providers (shared across viewers)
const providerCache = {
  googleBase: null as any,
  googleOverlay: null as any,
  mapbox: new Map<string, MapboxStyleImageryProvider>(),
}

// Cache for imagery layers per viewer (layers can only belong to one viewer)
const layerCache = new WeakMap<
  Viewer,
  Map<string, { base: ImageryLayer; overlay?: ImageryLayer }>
>()

// Get or create the layer cache for a specific viewer
const getViewerCache = (viewer: Viewer) => {
  if (!layerCache.has(viewer)) {
    layerCache.set(viewer, new Map())
  }
  return layerCache.get(viewer)!
}

// Create Google 2D imagery layers (with provider caching)
export const setGoogle2DImagery = async () => {
  // Cache providers (these can be shared)
  if (!providerCache.googleBase) {
    providerCache.googleBase = await Google2DImageryProvider.fromIonAssetId({
      assetId: '3830184',
      mapType: 'satellite',
    })
  }
  if (!providerCache.googleOverlay) {
    providerCache.googleOverlay = await Google2DImageryProvider.fromIonAssetId({
      assetId: '3830184',
      overlayLayerType: 'layerRoadmap',
    })
  }

  // Create new layers (layers cannot be shared between viewers)
  const base = new ImageryLayer(providerCache.googleBase as any, {
    brightness: 1.1,
    contrast: 1.15,
    saturation: 1.2,
    gamma: 1.0,
  })
  const overlay = new ImageryLayer(providerCache.googleOverlay as any)
  return { base, overlay }
}

// Create Mapbox imagery provider (with caching)
export const setMapboxImagery = async (url: string) => {
  if (!providerCache.mapbox.has(url)) {
    const mapboxStyle = new MapboxStyleImageryProvider({
      styleId: url,
      accessToken: import.meta.env.VITE_APP_KEY,
      minimumLevel: 1,
      maximumLevel: 22,
    })
    providerCache.mapbox.set(url, mapboxStyle)
  }
  return providerCache.mapbox.get(url)!
}

// Switch map style with layer caching (hide/show instead of remove/recreate)
export const switchMapStyle = async (viewer: Viewer, url: string) => {
  const cache = getViewerCache(viewer)

  // Hide all existing layers
  for (let i = 0; i < viewer.imageryLayers.length; i++) {
    viewer.imageryLayers.get(i).show = false
  }

  // Check if we have cached layers for this style
  if (cache.has(url)) {
    log.info('Using cached layers for style:', url)
    const cached = cache.get(url)!
    cached.base.show = true
    if (cached.overlay) {
      cached.overlay.show = true
    }
    return
  }

  // Create and cache new layers
  if (url === 'google-maps') {
    const { base, overlay } = await setGoogle2DImagery()
    viewer.imageryLayers.add(base)
    viewer.imageryLayers.add(overlay)
    cache.set(url, { base, overlay })
  } else {
    const provider = await setMapboxImagery(url)
    const layer = viewer.imageryLayers.addImageryProvider(provider)
    cache.set(url, { base: layer })
  }
}

// Clear layer cache for a viewer (call on cleanup)
export const clearLayerCache = (viewer: Viewer) => {
  layerCache.delete(viewer)
}

// Clear all provider caches (for full reset)
export const clearAllCaches = () => {
  providerCache.googleBase = null
  providerCache.googleOverlay = null
  providerCache.mapbox.clear()
}
